import React, { useState, useEffect } from 'react';
import Navbar from '../components/Navbar';
import { getSummary, getExpenses } from '../utils/api';
import { Bar } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend
} from 'chart.js';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

function Report() {
    const [summaries, setSummaries] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchLast6Months();
    }, []);

    const fetchLast6Months = async () => {
        const months = [];
        for (let i = 5; i >= 0; i--) {
            const d = new Date();
            d.setMonth(d.getMonth() - i);
            months.push(d.toISOString().slice(0, 7));
        }

        try {
            const results = await Promise.all(months.map(m => getSummary(m)));
            setSummaries(months.map((m, i) => ({
                month: m,
                total: results[i].data.total,
                by_category: results[i].data.by_category,
                count: results[i].data.count
            })));
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const barData = {
        labels: summaries.map(s => s.month),
        datasets: [{
            label: 'Total Spent (₹)',
            data: summaries.map(s => s.total),
            backgroundColor: 'rgba(108, 99, 255, 0.7)',
            borderColor: '#6c63ff',
            borderWidth: 2,
            borderRadius: 6,
        }]
    };

    const barOptions = {
        responsive: true,
        plugins: {
            legend: {
                labels: { color: '#e2e8f0' }
            },
            title: {
                display: false,
            }
        },
        scales: {
            x: {
                ticks: { color: '#8892a4' },
                grid: { color: 'rgba(255,255,255,0.05)' }
            },
            y: {
                ticks: { color: '#8892a4' },
                grid: { color: 'rgba(255,255,255,0.05)' }
            }
        }
    };

    const currentMonth = summaries[summaries.length - 1];
    const prevMonth = summaries[summaries.length - 2];
    const diff = currentMonth && prevMonth
        ? currentMonth.total - prevMonth.total
        : 0;

    return (
        <div>
            <Navbar />
            <div className="main-content">
                <div className="page-title">Monthly <span>Reports</span></div>

                {loading ? (
                    <p style={{ color: 'var(--text2)' }}>Loading reports...</p>
                ) : (
                    <>
                        {/* Summary Cards */}
                        <div className="stats-row">
                            <div className="stat-card">
                                <div className="stat-label">This Month</div>
                                <div className="stat-value">
                                    ₹{currentMonth?.total.toLocaleString() || 0}
                                </div>
                            </div>
                            <div className="stat-card">
                                <div className="stat-label">Last Month</div>
                                <div className="stat-value">
                                    ₹{prevMonth?.total.toLocaleString() || 0}
                                </div>
                            </div>
                            <div className="stat-card">
                                <div className="stat-label">vs Last Month</div>
                                <div className="stat-value" style={{ color: diff > 0 ? '#ff4757' : '#00d4aa' }}>
                                    {diff > 0 ? '+' : ''}₹{Math.abs(diff).toLocaleString()}
                                </div>
                            </div>
                            <div className="stat-card">
                                <div className="stat-label">6 Month Total</div>
                                <div className="stat-value">
                                    ₹{summaries.reduce((a, b) => a + b.total, 0).toLocaleString()}
                                </div>
                            </div>
                        </div>

                        {/* Bar Chart */}
                        <div className="chart-card" style={{ marginBottom: '24px' }}>
                            <h3>Spending — Last 6 Months</h3>
                            <Bar data={barData} options={barOptions} />
                        </div>

                        {/* Month by Month Breakdown */}
                        <div className="card">
                            <h3 style={{ marginBottom: '16px', fontSize: '0.95rem', color: 'var(--text2)' }}>
                                MONTH BY MONTH BREAKDOWN
                            </h3>
                            <div className="expense-list">
                                {summaries.slice().reverse().map(s => (
                                    <div key={s.month} className="expense-item">
                                        <div className="expense-left">
                                            <span className="expense-category">{s.month}</span>
                                            <div className="expense-note">
                                                {s.count} transactions · Top: {
                                                    Object.keys(s.by_category).length > 0
                                                        ? Object.entries(s.by_category).sort((a, b) => b[1] - a[1])[0][0]
                                                        : '—'
                                                }
                                            </div>
                                        </div>
                                        <div className="expense-amount">₹{s.total.toLocaleString()}</div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}

export default Report;